import React from 'react';
import { Link } from 'react-router-dom';

import { pipe } from 'fp-ts/function';
import * as O from 'fp-ts/Option';
import { useAtomValue, useSetAtom } from 'jotai';

import { SideMenuButton } from './SideMenuButton';

import BsLogoMenu from '../../assets/images/bsLogoMenu.svg';
import deckIcon from '../../assets/images/deckIcon.svg';
import feedbackIcon from '../../assets/images/feedbackIcon.svg';
import homeIcon from '../../assets/images/homeIcon.svg';
import logoutIcon from '../../assets/images/logoutIcon.svg';
import menuIcon from '../../assets/images/menu.svg';
import performanceIcon from '../../assets/images/performanceIcon.svg';
import settingsIcon from '../../assets/images/settingsIcon.svg';
import tourIcon from '../../assets/images/tourIcon.svg';
import { useIsAssessmentCompleted } from '../../hooks/useIsAssessmentCompleted';
import { User } from '../../model/domain/User';
import { sideMenuForceOpen } from '../../model/state/sideMenuState';
import { userState } from '../../model/state/userState';

type SideMenuContentProps = {
  showMenu: boolean;
};

export const SideMenuContent: React.FC<SideMenuContentProps> = ({ showMenu }) => {
  const maybeUser = useAtomValue(userState);
  const user = O.toNullable(maybeUser);
  const libraryEnabled = user?.partner.enableLibrary;
  const performanceEnabled = user?.partner.enableUserPerformance;
  const isAssessmentCompleted = useIsAssessmentCompleted();
  const setForceShowMenu = useSetAtom(sideMenuForceOpen);

  const shownLibraryTour = pipe(
    maybeUser,
    O.map((u: User) => u.libraryTourShown),
    O.getOrElse<boolean>(() => false)
  );

  const upperMenuButtons: SideMenuButtonAttributes[] = [
    {
      name: "Today's Cards",
      link: '/welcomeBack',
      paths: ['/study'],
      isDisabled: false,
      icon: homeIcon,
    },
    {
      name: 'My Performance',
      link: '/performance',
      isDisabled: !isAssessmentCompleted,
      icon: performanceIcon,
      isInvisible: !performanceEnabled,
    },
    {
      name: 'Library',
      link: shownLibraryTour ? '/library' : '/library_tour',
      isDisabled: !isAssessmentCompleted,
      icon: deckIcon,
      isInvisible: !libraryEnabled,
    },
  ];

  interface SideMenuButtonAttributes {
    name: string;
    link: string;
    paths?: string[];
    isDisabled: boolean;
    isInvisible?: boolean;
    icon?: string;
  }

  const lowerMenuButtons: SideMenuButtonAttributes[] = [
    {
      name: 'Tour',
      link: '/tour',
      isDisabled: false,
      icon: tourIcon,
    },
    {
      name: 'Feedback',
      link: '/feedback',
      isDisabled: false,
      icon: feedbackIcon,
    },
    {
      name: 'Settings',
      link: '/settings',
      paths: ['/updatePws', '/preferences'],
      isDisabled: false,
      icon: settingsIcon,
    },
    // {
    //   name: 'FAQ',
    //   link: '/faq',
    //   isDisabled: false,
    // },
  ];

  const logoutMenuButton: SideMenuButtonAttributes = {
    name: 'Logout',
    link: '/logout',
    isDisabled: false,
    icon: logoutIcon,
  };

  return (
    <div className="flex h-screen flex-col overflow-y-hidden">
      <div className="flex items-center pb-2 pl-[18px] pt-[52px]">
        <img
          onClick={() => {
            setForceShowMenu((old) => !old);
          }}
          className="mr-2 block h-[22px] w-[22px] object-contain"
          alt="menu"
          src={menuIcon}
        />
        {showMenu && (
          <Link to="/welcomeBack" className="pl-2.5">
            <img alt="Blank Slate" src={BsLogoMenu} />
          </Link>
        )}
      </div>
      <div className="relative flex h-full flex-col justify-between">
        <div className="flex flex-col gap-[13px] px-[9px] pt-[58px]">
          {upperMenuButtons
            .filter((btn) => !btn.isInvisible)
            .map((btn) => {
              return (
                <SideMenuButton key={btn.name} sideMenuButtonAttributes={btn} showMenu={showMenu} />
              );
            })}
        </div>
        <div className="flex flex-col gap-[7px] px-[9px]">
          {lowerMenuButtons.map((btn) => {
            return (
              <SideMenuButton key={btn.name} sideMenuButtonAttributes={btn} showMenu={showMenu} />
            );
          })}
        </div>
        <div className="mb-[200px] mt-2.5 flex flex-col gap-[7px] px-[9px]">
          <SideMenuButton sideMenuButtonAttributes={logoutMenuButton} showMenu={showMenu} />
        </div>
      </div>
    </div>
  );
};
